import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, CheckIcon, ChevronUpIcon, CircleAlertIcon, NotebookTextIcon, PlayIcon, ScanLineIcon } from 'lucide-react';
import { Logo } from './Logo';
import { OrderWorkflow } from './OrderWorkflow';
type ScheduledOrdersProps = {
  onBack: () => void;
  isInitialLoading?: boolean;
};
type OrderMaterial = {
  articleNumber: string;
  description: string;
  quantityPerLotKg: number;
  isWeighed?: boolean;
};
type ScheduledOrder = {
  id: string;
  product: string;
  lots: number;
  materials: OrderMaterial[];
};
const scheduledOrders: ScheduledOrder[] = [{
  id: 'AUF-240718-031',
  product: 'Backdekoration',
  lots: 12,
  materials: [{
    articleNumber: 'BD-1042',
    description: 'Zuckerstreusel, bunt',
    quantityPerLotKg: 120
  }, {
    articleNumber: 'BD-1178',
    description: 'Schokoraspel, fein',
    quantityPerLotKg: 80
  }, {
    articleNumber: 'BD-1231',
    description: 'Dekorzucker, weiß',
    quantityPerLotKg: 50
  }]
}, {
  id: 'AUF-240718-032',
  product: 'Binde- & Geliermittel',
  lots: 8,
  materials: [{
    articleNumber: 'BG-2014',
    description: 'Pektin, Apfel',
    quantityPerLotKg: 300
  }, {
    articleNumber: 'BG-2039',
    description: 'Agar-Agar, Pulver',
    quantityPerLotKg: 125
  }, {
    articleNumber: 'BG-2097',
    description: 'Johannisbrotkernmehl',
    quantityPerLotKg: 75
  }]
}, {
  id: 'AUF-240718-033',
  product: 'Bioland Honige',
  lots: 16,
  materials: [{
    articleNumber: 'BH-3008',
    description: 'Blütenhonig, Bioland',
    quantityPerLotKg: 150
  }, {
    articleNumber: 'BH-3041',
    description: 'Waldhonig, Bioland',
    quantityPerLotKg: 100
  }]
}, {
  id: 'AUF-240718-034',
  product: 'Öle & Fette',
  lots: 10,
  materials: [{
    articleNumber: 'OF-4022',
    description: 'Sonnenblumenöl, kaltgepresst',
    quantityPerLotKg: 350
  }, {
    articleNumber: 'OF-4056',
    description: 'Kakaobutter, Bio',
    quantityPerLotKg: 150
  }]
}, {
  id: 'AUF-240718-035',
  product: 'Brotaufstriche',
  lots: 20,
  materials: [{
    articleNumber: 'BA-5013',
    description: 'Haselnussmus, geröstet',
    quantityPerLotKg: 140
  }, {
    articleNumber: 'BA-5064',
    description: 'Kakaopulver, stark entölt',
    quantityPerLotKg: 60
  }, {
    articleNumber: 'BA-5098',
    description: 'Rohrzucker, fein',
    quantityPerLotKg: 50
  }]
}, {
  id: 'AUF-240718-036',
  product: 'Fruchtmischung Mustercharge',
  lots: 1,
  materials: [{
    articleNumber: 'FM-6012',
    description: 'Apfelstücke, getrocknet',
    quantityPerLotKg: 45
  }, {
    articleNumber: 'FM-6048',
    description: 'Cranberries, gesüßt',
    quantityPerLotKg: 30
  }, {
    articleNumber: 'FM-6073',
    description: 'Kürbiskerne, geröstet',
    quantityPerLotKg: 18
  }, {
    articleNumber: 'FM-6095',
    description: 'Zimt, gemahlen',
    quantityPerLotKg: 2.5
  }]
}];
export function ScheduledOrders({
  onBack,
  isInitialLoading = false
}: ScheduledOrdersProps) {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [startedOrderId, setStartedOrderId] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [isScannerDialogOpen, setIsScannerDialogOpen] = useState(false);
  const [isScannerScanning, setIsScannerScanning] = useState(false);
  const [scanAttempt, setScanAttempt] = useState(0);
  const [isListLoading, setIsListLoading] = useState(isInitialLoading);
  const scannedOrder = scheduledOrders[0];
  const isScanResultFound = scanAttempt % 2 === 1;
  useEffect(() => {
    if (!isScannerDialogOpen || !isScannerScanning) {
      return;
    }
    const scanTimeoutId = window.setTimeout(() => {
      setIsScannerScanning(false);
    }, 3000);
    return () => window.clearTimeout(scanTimeoutId);
  }, [isScannerDialogOpen, isScannerScanning]);
  useEffect(() => {
    if (!isListLoading) {
      return;
    }
    const listTimeoutId = window.setTimeout(() => {
      setIsListLoading(false);
    }, 2000);
    return () => window.clearTimeout(listTimeoutId);
  }, [isListLoading]);
  function openScannerDialog() {
    setScanAttempt(currentAttempt => currentAttempt + 1);
    setIsScannerScanning(true);
    setIsScannerDialogOpen(true);
  }
  function closeScannerDialog() {
    setIsScannerDialogOpen(false);
    setIsScannerScanning(false);
  }
  function selectOrder(order: ScheduledOrder) {
    setSelectedOrderId(order.id);
  }
  function startOrder(order: ScheduledOrder) {
    setSelectedOrderId(order.id);
    setStartedOrderId(order.id);
  }
  function startScannedOrder() {
    startOrder(scannedOrder);
    closeScannerDialog();
  }
  function toggleMaterials(orderId: string) {
    setExpandedOrderId(currentOrderId => currentOrderId === orderId ? null : orderId);
  }
  const selectedOrder = scheduledOrders.find(order => order.id === (startedOrderId ?? selectedOrderId));
  return <main className="min-h-screen w-full bg-surface-subtle font-body text-text-primary" data-id="element-1121">
      <header className="border-b border-border bg-surface px-5 py-4 sm:px-8 md:px-10 lg:px-14" data-id="element-1122">
        <div className="flex items-center justify-between" data-id="element-1123">
          <Logo size="lg" alt="Sonnentracht" className="h-[52px] w-auto sm:h-[60px]" data-id="element-1124" />
          {startedOrderId && selectedOrder && <div className="flex flex-col text-right" data-id="element-1125">
              <div className="font-heading text-lg sm:text-xl" data-id="element-1126">
                {selectedOrder.id}
              </div>
              <p className="text-sm text-text-secondary" data-id="element-1127">
                {selectedOrder.product}
              </p>
            </div>}
        </div>
      </header>

      {startedOrderId && selectedOrder ? <OrderWorkflow order={selectedOrder} onCancel={() => setStartedOrderId(null)} data-id="element-1128" /> : <section className="mx-auto w-full max-w-[1540px] px-5 py-6 sm:px-8 sm:py-8 md:px-10 lg:px-14 xl:px-20" data-id="element-1129">
          <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} data-id="element-1130">
            <div className="flex flex-col gap-5 border-b border-border pb-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-1131">
              <div className="flex min-w-0 items-center gap-4 sm:gap-5" data-id="element-1132">
                <button type="button" onClick={onBack} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-border bg-surface text-text-primary transition-colors hover:bg-brand-yellow-light focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green active:bg-brand-green" aria-label="Zurück zum Produktionsmenü" data-id="element-1133">
                  <ArrowLeftIcon size={26} strokeWidth={1.9} aria-hidden="true" data-id="element-1134" />
                </button>
                <div data-id="element-1135">
                  <h1 className="font-heading text-3xl leading-tight sm:text-4xl" data-id="element-1136">
                    Geplante Aufträge
                  </h1>
                </div>
              </div>

              <button type="button" onClick={openScannerDialog} aria-haspopup="dialog" aria-expanded={isScannerDialogOpen} className="flex min-h-[72px] items-center gap-4 rounded-lg border border-border bg-surface px-4 py-3 text-left shadow-sm transition-colors hover:border-brand-green hover:bg-brand-yellow-light focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green active:bg-brand-green sm:px-5 lg:w-[360px]" data-id="element-1137">
                <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-brand-yellow-light" data-id="element-1138">
                  <ScanLineIcon className="text-text-primary" size={24} strokeWidth={1.7} aria-hidden="true" data-id="element-1139" />
                  <motion.span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-surface bg-brand-green" animate={{
                scale: [1, 1.25, 1],
                opacity: [1, 0.65, 1]
              }} transition={{
                duration: 2,
                ease: 'easeInOut',
                repeat: Infinity
              }} aria-hidden="true" data-id="element-1140" />
                </div>
                <span className="min-w-0" data-id="element-1141">
                  <span className="block font-heading text-base text-text-primary" data-id="element-1142">
                    Mit Handscanner starten
                  </span>
                  <span className="mt-0.5 block text-sm leading-5 text-text-secondary" data-id="element-1143">
                    Auftrag scannen und direkt starten.
                  </span>
                </span>
              </button>
            </div>

            <ul className="mt-5 grid grid-cols-1 gap-4 pb-8" aria-busy={isListLoading} aria-label={isListLoading ? 'Geplante Aufträge werden geladen' : 'Freigegebene Aufträge'} data-id="element-1144">
              {isListLoading ? <ScheduledOrdersSkeleton data-id="element-1145" /> : scheduledOrders.map((order, index) => {
            const isSelected = selectedOrderId === order.id;
            const isStarted = startedOrderId === order.id;
            const isExpanded = expandedOrderId === order.id;
            const weightPerLotKg = order.materials.reduce((total, material) => total + material.quantityPerLotKg, 0);
            const totalWeightKg = weightPerLotKg * order.lots;
            const detailId = `${order.id}-materials`;
            return <motion.li key={order.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              delay: index * 0.05,
              ease: 'easeOut'
            }} className={`rounded-lg border bg-surface p-3 transition-shadow sm:p-4 ${isSelected ? 'border-brand-green shadow-md' : 'border-border shadow-sm hover:border-text-secondary'}`} data-id="element-1146">
                      <article className="flex min-h-[124px] items-stretch gap-2 sm:gap-4" data-id="element-1147">
                        <button type="button" onClick={() => selectOrder(order)} aria-pressed={isSelected} className="flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 text-left transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green hover:bg-surface-subtle sm:gap-4 sm:px-3" data-id="element-1148">
                          <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-md font-heading text-lg ${isSelected ? 'bg-brand-green' : 'border border-border bg-surface-subtle'}`} aria-hidden="true" data-id="element-1149">
                            {index + 1}
                          </span>

                          <span className="min-w-0 flex-1" data-id="element-1150">
                            <span className="font-heading text-base sm:text-lg" data-id="element-1151">
                              {order.id}
                            </span>
                            <span className="mt-1 block truncate text-sm text-text-secondary sm:text-base" data-id="element-1152">
                              {order.product}
                            </span>
                            <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-secondary" data-id="element-1153">
                              <span data-id="element-1154">
                                {order.lots} × {formatWeight(weightPerLotKg)} kg
                              </span>
                              <span aria-hidden="true" data-id="element-1155">·</span>
                              <span data-id="element-1156">
                                Gesamt:{' '}
                                <span className="font-heading text-text-primary" data-id="element-1157">
                                  {formatWeight(totalWeightKg)} kg
                                </span>
                              </span>
                            </span>
                            <span className="mt-3 flex items-center gap-2.5" data-id="element-1158">
                              <span className="h-2 w-20 overflow-hidden rounded-full bg-border sm:w-32" aria-hidden="true" data-id="element-1159">
                                <span className="block h-full w-0 rounded-full bg-brand-green" data-id="element-1160" />
                              </span>
                              <span className="font-heading text-sm" data-id="element-1161">
                                0 / {order.lots.toLocaleString('de-DE')}
                              </span>
                              <span className="hidden text-sm text-text-secondary sm:inline" data-id="element-1162">
                                Lots
                              </span>
                            </span>
                          </span>
                        </button>

                        <button type="button" onClick={() => toggleMaterials(order.id)} aria-expanded={isExpanded} aria-controls={detailId} aria-label={isExpanded ? `Rezeptur für ${order.product} schließen` : `Rezeptur für ${order.product} öffnen`} className={`flex min-h-[56px] w-14 shrink-0 items-center justify-center self-center rounded-md border transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green active:scale-95 sm:h-16 sm:w-16 ${isExpanded ? 'border-brand-green bg-brand-yellow-light text-text-primary' : 'border-border bg-surface text-text-secondary hover:bg-surface-subtle'}`} data-id="element-1163">
                          {isExpanded ? <ChevronUpIcon size={26} strokeWidth={2} aria-hidden="true" data-id="element-1164" /> : <NotebookTextIcon size={24} strokeWidth={1.9} aria-hidden="true" data-id="element-1165" />}
                        </button>

                        <button type="button" onClick={() => startOrder(order)} className={`flex min-h-[56px] w-14 shrink-0 items-center justify-center self-center rounded-md transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green active:scale-95 sm:h-16 sm:w-16 ${isStarted ? 'bg-success text-surface' : 'bg-brand-green text-text-primary hover:bg-brand-green-dark'}`} aria-label={isStarted ? `${order.id} ausgewählt` : `${order.id} zur Abfüllung auswählen`} data-id="element-1166">
                          {isStarted ? <CheckCircle2Icon size={25} strokeWidth={2} aria-hidden="true" data-id="element-1167" /> : <PlayIcon className="ml-0.5" size={25} fill="currentColor" strokeWidth={1.8} aria-hidden="true" data-id="element-1168" />}
                        </button>
                      </article>

                      <AnimatePresence initial={false} data-id="element-1169">
                        {isExpanded && <motion.section id={detailId} aria-label={`Rezeptur für ${order.product}`} initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.24,
                  ease: 'easeOut'
                }} className="overflow-hidden" data-id="element-1170">
                            <div className="mt-4 border-t border-border pt-4" data-id="element-1171">
                              <div className="overflow-x-auto" data-id="element-1172">
                                <table className="w-full min-w-[540px] text-left text-sm" data-id="element-1173">
                                  <thead className="text-xs uppercase tracking-wide text-text-secondary" data-id="element-1174">
                                    <tr data-id="element-1175">
                                      <th scope="col" className="pb-2 pr-4 font-normal" data-id="element-1176">
                                        Artikelnummer
                                      </th>
                                      <th scope="col" className="pb-2 pr-4 font-normal" data-id="element-1177">
                                        Beschreibung
                                      </th>
                                      <th scope="col" className="whitespace-nowrap pb-2 pr-4 text-center font-normal" data-id="element-1178">
                                        Verwogen
                                      </th>
                                      <th scope="col" className="whitespace-nowrap pb-2 pr-4 text-right font-normal" data-id="element-1179">
                                        Pro Lot
                                      </th>
                                      <th scope="col" className="whitespace-nowrap pb-2 text-right font-normal" data-id="element-1180">
                                        Pro Charge
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-1181">
                                    {order.materials.map(material => {
                            const materialTotalWeightKg = material.quantityPerLotKg * order.lots;
                            return <tr key={material.articleNumber} className="border-t border-border" data-id="element-1182">
                                          <td className="whitespace-nowrap py-3 pr-4 font-heading" data-id="element-1183">
                                            {material.articleNumber}
                                          </td>
                                          <td className="py-3 pr-4 text-text-secondary" data-id="element-1184">
                                            {material.description}
                                          </td>
                                          <td className="py-3 pr-4 text-center" data-id="element-1185">
                                            {material.isWeighed ? <span className="inline-flex items-center justify-center text-success" aria-label="Verwogen" data-id="element-1186">
                                                <CheckIcon size={20} strokeWidth={2.6} aria-hidden="true" data-id="element-1187" />
                                                <span className="sr-only" data-id="element-1188">
                                                  Verwogen
                                                </span>
                                              </span> : <span className="font-heading text-base text-text-secondary" aria-label="Noch nicht verwogen" data-id="element-1189">
                                                <span aria-hidden="true" data-id="element-1190">
                                                  −
                                                </span>
                                                <span className="sr-only" data-id="element-1191">
                                                  Noch nicht verwogen
                                                </span>
                                              </span>}
                                          </td>
                                          <td className="whitespace-nowrap py-3 pr-4 text-right font-heading" data-id="element-1192">
                                            {formatWeight(material.quantityPerLotKg)}{' '}
                                            kg
                                          </td>
                                          <td className="whitespace-nowrap py-3 text-right font-heading" data-id="element-1193">
                                            {formatWeight(materialTotalWeightKg)}{' '}
                                            kg
                                          </td>
                                        </tr>;
                          })}
                                  </tbody>
                                  <tfoot data-id="element-1194">
                                    <tr className="border-t-2 border-text-primary" data-id="element-1195">
                                      <th scope="row" colSpan={3} className="pt-3 text-left font-heading" data-id="element-1196">
                                        Gesamtgewicht · {order.lots} Lots
                                      </th>
                                      <td className="whitespace-nowrap pt-3 pr-4 text-right font-heading" data-id="element-1197">
                                        {formatWeight(weightPerLotKg)} kg
                                      </td>
                                      <td className="whitespace-nowrap pt-3 text-right font-heading" data-id="element-1198">
                                        {formatWeight(totalWeightKg)} kg
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          </motion.section>}
                      </AnimatePresence>
                    </motion.li>;
          })}
            </ul>

            <AnimatePresence data-id="element-1199">
              {isScannerDialogOpen && <motion.div className="fixed inset-0 z-20 flex items-end justify-center bg-text-primary/50 p-4 backdrop-blur-md sm:items-center sm:p-8 lg:p-10" role="presentation" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onMouseDown={closeScannerDialog} data-id="element-1200">
                  <motion.section role="dialog" aria-modal="true" aria-labelledby="scanner-dialog-title" aria-describedby="scanner-dialog-description" className="w-full max-w-3xl overflow-hidden rounded-2xl border border-border bg-surface shadow-md" initial={{
              opacity: 0,
              y: 24,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: 20,
              scale: 0.98
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} onMouseDown={event => event.stopPropagation()} data-id="element-1201">
                    {isScannerScanning ? <div className="animate-pulse p-6 sm:p-8" role="status" aria-live="polite" aria-label="Handscanner sucht nach einem Auftrag" data-id="element-1202">
                        <div className="flex items-center gap-5 border-b border-border pb-6 sm:pb-8" data-id="element-1203">
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-brand-yellow-light" data-id="element-1204">
                            <ScanLineIcon size={31} strokeWidth={1.7} aria-hidden="true" data-id="element-1205" />
                          </div>

                          <div className="min-w-0 flex-1" data-id="element-1206">
                            <p className="text-base text-text-secondary" data-id="element-1207">
                              Handscanner · Auftrag wird gesucht
                            </p>

                            <div className="mt-3 h-9 w-52 rounded-md bg-border sm:w-72" data-id="element-1208" />
                            <div className="mt-3 h-5 w-36 rounded-md bg-border sm:w-52" data-id="element-1209" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 pt-6 sm:grid-cols-3 sm:pt-8" data-id="element-1210">
                          {[0, 1, 2].map(item => <div key={item} className={`rounded-lg border border-border bg-surface-subtle p-4 ${item === 2 ? 'col-span-2 sm:col-span-1' : ''}`} data-id="element-1211">
                              <div className="h-4 w-20 rounded bg-border" data-id="element-1212" />
                              <div className="mt-3 h-7 w-16 rounded bg-border" data-id="element-1213" />
                            </div>)}
                        </div>
                        <div className="mt-8" data-id="element-1214">
                          <div className="h-7 w-32 rounded-md bg-border" data-id="element-1215" />
                          <div className="mt-4 overflow-hidden rounded-lg border border-border" data-id="element-1216">
                            <div className="h-12 bg-surface-subtle" data-id="element-1217" />
                            <div className="space-y-3 p-4" data-id="element-1218">
                              {[0, 1, 2].map(item => <div key={item} className="h-6 w-full rounded bg-border" data-id="element-1219" />)}
                            </div>
                          </div>
                        </div>
                        <div className="mt-8 border-t border-border pt-5 text-center text-base text-text-secondary" data-id="element-1220">
                          Auftrag wird geladen...
                        </div>
                      </div> : isScanResultFound ? <>
                        <div className="flex items-start gap-5 border-b border-border bg-surface-subtle p-6 sm:p-8" data-id="element-1221">
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-brand-yellow-light" data-id="element-1222">
                            <ScanLineIcon size={31} strokeWidth={1.7} aria-hidden="true" data-id="element-1223" />
                          </div>
                          <div className="min-w-0" data-id="element-1224">
                            <h2 id="scanner-dialog-title" className="font-heading text-3xl leading-tight sm:text-4xl" data-id="element-1225">
                              {scannedOrder.id}
                            </h2>
                            <p id="scanner-dialog-description" className="mt-2 text-lg text-text-secondary" data-id="element-1226">
                              {scannedOrder.product}
                            </p>
                          </div>
                        </div>

                        <div className="max-h-[56vh] overflow-y-auto p-6 sm:p-8" data-id="element-1227">
                          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-id="element-1228">
                            <div className="rounded-lg border border-border bg-surface-subtle p-4" data-id="element-1229">
                              <dt className="text-sm uppercase tracking-wide text-text-secondary" data-id="element-1230">
                                Lots
                              </dt>
                              <dd className="mt-1.5 font-heading text-2xl" data-id="element-1231">
                                {scannedOrder.lots}
                              </dd>
                            </div>
                            <div className="rounded-lg border border-border bg-surface-subtle p-4" data-id="element-1232">
                              <dt className="text-sm uppercase tracking-wide text-text-secondary" data-id="element-1233">
                                Gewicht pro Lot
                              </dt>
                              <dd className="mt-1.5 font-heading text-2xl" data-id="element-1234">
                                {formatWeight(scannedOrder.materials.reduce((total, material) => total + material.quantityPerLotKg, 0))}{' '}
                                kg
                              </dd>
                            </div>
                            <div className="col-span-2 rounded-lg border border-border bg-surface-subtle p-4 sm:col-span-1" data-id="element-1235">
                              <dt className="text-sm uppercase tracking-wide text-text-secondary" data-id="element-1236">
                                Gesamtgewicht
                              </dt>
                              <dd className="mt-1.5 font-heading text-2xl" data-id="element-1237">
                                {formatWeight(scannedOrder.materials.reduce((total, material) => total + material.quantityPerLotKg, 0) * scannedOrder.lots)}{' '}
                                kg
                              </dd>
                            </div>
                          </dl>

                          <section className="mt-8" aria-labelledby="materials-title" data-id="element-1238">
                            <h3 id="materials-title" className="font-heading text-xl text-text-primary" data-id="element-1239">
                              Materialien
                            </h3>
                            <div className="mt-4 overflow-x-auto rounded-lg border border-border" data-id="element-1240">
                              <table className="w-full min-w-[560px] text-left text-base" data-id="element-1241">
                                <thead className="bg-surface-subtle text-sm uppercase tracking-wide text-text-secondary" data-id="element-1242">
                                  <tr data-id="element-1243">
                                    <th scope="col" className="px-4 py-3 font-normal" data-id="element-1244">
                                      Artikelnummer
                                    </th>
                                    <th scope="col" className="px-4 py-3 font-normal" data-id="element-1245">
                                      Beschreibung
                                    </th>
                                    <th scope="col" className="whitespace-nowrap px-4 py-3 text-right font-normal" data-id="element-1246">
                                      Pro Lot
                                    </th>
                                    <th scope="col" className="whitespace-nowrap px-4 py-3 text-right font-normal" data-id="element-1247">
                                      Gesamt
                                    </th>
                                  </tr>
                                </thead>
                                <tbody data-id="element-1248">
                                  {scannedOrder.materials.map(material => <tr key={material.articleNumber} className="border-t border-border" data-id="element-1249">
                                      <td className="whitespace-nowrap px-4 py-3 font-heading" data-id="element-1250">
                                        {material.articleNumber}
                                      </td>
                                      <td className="px-4 py-3 text-text-secondary" data-id="element-1251">
                                        {material.description}
                                      </td>
                                      <td className="whitespace-nowrap px-4 py-3 text-right font-heading" data-id="element-1252">
                                        {formatWeight(material.quantityPerLotKg)}{' '}
                                        kg
                                      </td>
                                      <td className="whitespace-nowrap px-4 py-3 text-right font-heading" data-id="element-1253">
                                        {formatWeight(material.quantityPerLotKg * scannedOrder.lots)}{' '}
                                        kg
                                      </td>
                                    </tr>)}
                                </tbody>
                              </table>
                            </div>
                          </section>
                        </div>

                        <div className="flex flex-col-reverse gap-3 border-t border-border p-5 sm:flex-row sm:justify-end sm:p-6" data-id="element-1254">
                          <button type="button" onClick={closeScannerDialog} className="min-h-[56px] rounded-md border border-border bg-surface px-6 font-heading text-base text-text-primary transition-colors hover:bg-surface-subtle focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-1255">
                            Abbrechen
                          </button>
                          <button type="button" onClick={startScannedOrder} className="min-h-[64px] rounded-md bg-brand-green px-10 font-heading text-lg text-text-primary transition-colors hover:bg-brand-green-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green sm:min-w-[250px]" data-id="element-1256">
                            Auftrag starten
                          </button>
                        </div>
                      </> : <div className="p-6 text-center sm:p-10" data-id="element-1257">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-surface-subtle" data-id="element-1258">
                          <CircleAlertIcon className="text-text-secondary" size={32} strokeWidth={1.7} aria-hidden="true" data-id="element-1259" />
                        </div>
                        <h2 id="scanner-dialog-title" className="mt-6 font-heading text-3xl leading-tight sm:text-4xl" data-id="element-1260">
                          Auftrag nicht gefunden
                        </h2>
                        <p id="scanner-dialog-description" className="mx-auto mt-3 max-w-md text-lg leading-7 text-text-secondary" data-id="element-1261">
                          Für diesen Scan konnte kein geplanter Auftrag
                          zugeordnet werden. Bitte Etikett prüfen und erneut
                          scannen.
                        </p>
                        <button type="button" onClick={closeScannerDialog} className="mt-8 min-h-[56px] rounded-md border border-border bg-surface px-7 font-heading text-base text-text-primary transition-colors hover:bg-surface-subtle focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" data-id="element-1262">
                          Schließen
                        </button>
                      </div>}
                  </motion.section>
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </section>}
    </main>;
}
function ScheduledOrdersSkeleton() {
  return <>
      {[0, 1, 2, 3, 4].map(item => <li key={item} className="min-h-[148px] animate-pulse rounded-lg border border-border bg-surface p-3 shadow-sm sm:p-4" aria-hidden="true" data-id="element-1263">
          <div className="flex min-h-[116px] items-center gap-3 sm:gap-4" data-id="element-1264">
            <div className="h-12 w-12 shrink-0 rounded-md bg-border" data-id="element-1265" />
            <div className="min-w-0 flex-1" data-id="element-1266">
              <div className="h-5 w-44 max-w-full rounded bg-border sm:w-56" data-id="element-1267" />
              <div className="mt-3 h-4 w-32 rounded bg-border sm:w-48" data-id="element-1268" />
              <div className="mt-4 h-4 w-64 max-w-[80%] rounded bg-border" data-id="element-1269" />
              <div className="mt-4 h-2 w-28 rounded-full bg-border sm:w-40" data-id="element-1270" />
            </div>
            <div className="h-14 w-14 shrink-0 rounded-md bg-border sm:h-16 sm:w-16" data-id="element-1271" />
            <div className="h-14 w-14 shrink-0 rounded-md bg-border sm:h-16 sm:w-16" data-id="element-1272" />
          </div>
        </li>)}
    </>;
}
function formatWeight(weightKg: number) {
  return weightKg.toLocaleString('de-DE');
}